import React, { useState } from 'react'
import { CheckCircle2, XCircle, Mail } from 'lucide-react'
import styles from './PhishingQuizActivity.module.css'
import { resolvePhishingAnswer } from '@/utils/phishing'

interface PhishingQuizActivityProps {
  activityData: Record<string, any>
  disabled?: boolean
  onSubmitQuiz: (data: Record<string, any>) => Promise<{ success: boolean; error?: string }>
  onComplete?: () => void | Promise<void>
}

const EMAIL_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

/** Replace {first} / {email} placeholders with realistic dummy values */
const fillPlaceholders = (html: string) =>
  html.replace(/\{first\}/g, 'Alex').replace(/\{email\}/g, 'alex.student@school.edu')

export function PhishingQuizActivity({ activityData, disabled = false, onSubmitQuiz, onComplete }: PhishingQuizActivityProps) {
  const data = activityData || {}

  const [choiceMade, setChoiceMade] = useState<string | null>(null)
  const [showFromEmail, setShowFromEmail] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const correctAnswer = resolvePhishingAnswer(data)
  const isCorrect = choiceMade !== null && choiceMade === correctAnswer

  const handleChoice = async (choice: string) => {
    if (choiceMade !== null) return
    setChoiceMade(choice)
    const correct = choice === correctAnswer
    setSubmitting(true)
    try {
      await onSubmitQuiz({
        answers: [{
          question_id: data.question_id || data.id,
          choice,
          correct,
          ...(data.category ? { category: data.category } : {}),
        }],
        total_correct: correct ? 1 : 0,
        total_questions: 1,
      })
      setSubmitted(true)
    } catch {
      setSubmitted(true)
    } finally {
      setSubmitting(false)
    }
  }

  if (disabled && choiceMade === null) {
    return (
      <div style={{ fontFamily: EMAIL_FONT, padding: 8 }}>
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-subtle border border-theme-primary flex items-center justify-center">
            <svg className="w-8 h-8 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-theme-primary mb-2">Waiting for Next Activity</h3>
          <p className="text-theme-text text-sm">Your captain will start the next activity soon.</p>
        </div>
      </div>
    )
  }

  if (!data.subject && !data.question) return null

  const initials = (data.fromName || 'U')
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div style={{ fontFamily: EMAIL_FONT, padding: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Email client sandbox ── */}
      <div style={{ background: '#ffffff', color: '#202124', borderRadius: 12, overflow: 'hidden', border: '1px solid #dadce0', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)' }}>

        {/* Window chrome */}
        <div style={{ background: '#dee1e6', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 7, borderBottom: '1px solid #c4c7cc' }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ec6a5e' }} />
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#f4bf4f' }} />
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#61c554' }} />
          <span style={{ marginLeft: 12, fontSize: 12, color: '#5f6368', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Mail style={{ width: 13, height: 13 }} />
            Inbox
          </span>
        </div>

        {/* URL / chrome bar */}
        {data.chromeBar && (
          <div style={{ background: '#f1f3f4', padding: '6px 14px', borderBottom: '1px solid #dadce0', display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, background: '#fff', padding: '5px 12px', borderRadius: 20, fontSize: 12, color: '#5f6368', fontFamily: 'monospace', border: '1px solid #dadce0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
              {fillPlaceholders(data.chromeBar)}
            </div>
          </div>
        )}

        {/* Email header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #e8eaed' }}>
          <h3 style={{ fontSize: 18, fontWeight: 400, color: '#202124', margin: '0 0 14px', lineHeight: 1.4 }}>
            {data.subject || data.question}
          </h3>
          <div className={styles.emailMeta}>
            <div className={styles.emailMetaSender}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 14, fontWeight: 600, flexShrink: 0,
                backgroundColor: data.fromColor || '#1a73e8',
              }}>
                {initials}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#202124', display: 'block' }}>{data.fromName || 'Unknown Sender'}</span>
                <button
                  onClick={() => setShowFromEmail(e => !e)}
                  className={styles.emailToggleBtn}
                >
                  {showFromEmail
                    ? <>&lt;{data.fromEmail}&gt; <span style={{ fontSize: 10, color: '#5f6368' }}>&#9650; hide</span></>
                    : <><span style={{ color: '#5f6368' }}>&lt;{(data.fromName || '').toLowerCase().replace(' ', '.')}@...&gt;</span> <span style={{ fontSize: 10, color: '#1a73e8' }}>&#9660; show</span></>
                  }
                </button>
              </div>
            </div>
            {data.date && (
              <span className={styles.emailDate}>{data.date}</span>
            )}
          </div>
        </div>

        {/* Email body */}
        <div className="phishing-email-body" style={{ padding: '20px', fontSize: 14, lineHeight: 1.7, color: '#3c4043', maxHeight: 280, overflowY: 'auto' as const }}>
          {data.body ? (
            <div dangerouslySetInnerHTML={{ __html: fillPlaceholders(data.body) }} />
          ) : data.bodyText ? (
            <div style={{ whiteSpace: 'pre-line' as const }}>{fillPlaceholders(data.bodyText)}</div>
          ) : (
            <p style={{ color: '#9aa0a6', fontStyle: 'italic' }}>No email content</p>
          )}
        </div>
      </div>

      {/* ── Answer area (outside email sandbox) ── */}
      {choiceMade === null ? (
        <div className={styles.answerGrid}>
          <button
            onClick={() => handleChoice('phishing')}
            className={`${styles.choiceBtn} ${styles.choiceBtnPhishing}`}
          >
            Phishing
          </button>
          <button
            onClick={() => handleChoice('legitimate')}
            className={`${styles.choiceBtn} ${styles.choiceBtnLegitimate}`}
          >
            Legitimate
          </button>
        </div>
      ) : (
        /* Feedback panel */
        <div className="rounded-xl overflow-hidden border border-theme-border">
          {/* Result header */}
          <div className={`flex items-center gap-3 px-4 py-3 ${isCorrect ? 'bg-[var(--color-success-subtle)] border-b border-[var(--color-success)]/30' : 'bg-[var(--color-danger-subtle)] border-b border-[var(--color-danger)]/30'}`}>
            {isCorrect
              ? <CheckCircle2 className="w-5 h-5 text-[var(--color-success)] shrink-0" />
              : <XCircle className="w-5 h-5 text-[var(--color-danger)] shrink-0" />
            }
            <span className={`font-semibold text-sm ${isCorrect ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'}`}>
              {isCorrect
                ? (correctAnswer === 'phishing' ? 'Correct — This is a phishing email!' : 'Correct — This is a legitimate email!')
                : (correctAnswer === 'phishing' ? 'Not quite — This was actually a phishing email.' : 'Not quite — This was actually a legitimate email.')}
            </span>
          </div>

          {/* Explanation */}
          <div className="bg-surface px-4 py-3">
            <p className="text-xs text-theme-text leading-relaxed">{data.explanation || 'No explanation provided.'}</p>
          </div>

          {data.explanation_image_url && (
            <div className="px-4 py-3 border-t border-theme-border">
              <img src={data.explanation_image_url} alt="" className="max-w-full max-h-64 rounded-lg object-contain" />
            </div>
          )}

          {/* Hints / clues */}
          {data.hints && data.hints.length > 0 && (
            <div className="bg-surface px-4 py-3 space-y-2 border-t border-theme-border">
              <p className="text-xs font-semibold text-theme-text-muted uppercase tracking-wide">What to look for:</p>
              <ul className="space-y-2">
                {data.hints.map((clue: string, i: number) => (
                  <li key={i} className="flex gap-2 text-xs text-theme-text leading-snug">
                    <span className="text-[var(--color-secondary)] font-bold shrink-0 mt-0.5">→</span>
                    <span>{clue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Advance button — self-paced only; in live mode the host controls progression */}
          {submitted && onComplete && (
            <div className="px-4 py-3 bg-surface border-t border-theme-border">
              <button
                onClick={() => onComplete()}
                disabled={submitting}
                className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-on-primary)]"
                style={{ background: 'var(--gradient-primary)' }}
              >
                Start next activity →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
